import {
  RELATION_HAS_MEMBER,
  stringifyEntityRef,
  type UserEntity,
} from "@backstage/catalog-model";
import { Progress } from "@backstage/core-components";
import {
  EntityRefLink,
  useEntity,
  useRelatedEntities,
} from "@backstage/plugin-catalog-react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import GroupIcon from "@material-ui/icons/Group";
import PersonIcon from "@material-ui/icons/Person";
import SearchIcon from "@material-ui/icons/Search";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import type { FC } from "react";
import { useState } from "react";

// ─── Annotation keys ──────────────────────────────────────────────────────────
// Mantenidas en sync con las constantes de ownershipUtils.ts (backend).

/** Owners asignados explícitamente a este grupo en Azure AD. */
const ANNOTATION_OWNERS_DIRECT = "graph.microsoft.com/owners-direct";
/** Owners heredados de grupos ancestros. */
const ANNOTATION_OWNERS_INHERITED = "graph.microsoft.com/owners-inherited";
/** Miembros que pertenecen directamente a este grupo (AD + owners efectivos). */
const ANNOTATION_MEMBERS_INHERITED = "graph.microsoft.com/members-inherited";

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Categoría de visualización de un miembro en la tarjeta.
 *
 * Prioridad de clasificación: owner > member.
 *   - "owner-direct"    → owner explícito de este grupo en Azure AD.
 *   - "owner-inherited" → owner heredado de un grupo ancestro.
 *   - "member-inherited"→ pertenece a un grupo descendiente (no owner).
 *   - "member-direct"   → pertenece directamente a este grupo (no owner).
 */
type MemberCategory =
  "owner-direct" | "owner-inherited" | "member-inherited" | "member-direct";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseRefSet(value: string | undefined): Set<string> {
  if (!value) return new Set();
  return new Set(
    value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

/**
 * Clasifica a un usuario en una de las 4 categorías, aplicando
 * la regla de prioridad: owner > member.
 *
 * Si un usuario es owner y a la vez miembro heredado, se muestra
 * como owner (la categoría más específica de responsabilidad).
 */
function getCategory(
  entity: UserEntity,
  ownersDirect: Set<string>,
  ownersInherited: Set<string>,
  membersInherited: Set<string>,
): MemberCategory {
  const ref = stringifyEntityRef(entity);
  if (ownersDirect.has(ref)) return "owner-direct";
  if (ownersInherited.has(ref)) return "owner-inherited";
  if (membersInherited.has(ref)) return "member-inherited";
  return "member-direct";
}

function matchesSearch(entity: UserEntity, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const name = entity.metadata.name.toLowerCase();
  const displayName = entity.spec?.profile?.displayName?.toLowerCase() ?? "";
  const email = entity.spec?.profile?.email?.toLowerCase() ?? "";
  return name.includes(q) || displayName.includes(q) || email.includes(q);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  search: {
    marginBottom: theme.spacing(2),
  },
  memberCard: {
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    padding: theme.spacing(2, 1.5),
    height: "100%",
  },
  avatarWrap: {
    position: "relative",
    display: "inline-flex",
    marginBottom: theme.spacing(1),
  },
  avatar: {
    width: 56,
    height: 56,
  },
  badgeIcon: {
    position: "absolute",
    bottom: -4,
    right: -6,
    fontSize: 18,
    filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
  },
  ownerDirectIcon: {
    color: "#f59e0b", // amber-400
  },
  ownerInheritedIcon: {
    color: theme.palette.text.secondary,
  },
  memberDirectIcon: {
    color: "#3b82f6", // blue-500
  },
  memberInheritedIcon: {
    color: theme.palette.type === "dark" ? "#93c5fd" : "#60a5fa", // blue-300/400
  },
  chip: {
    height: 20,
    fontSize: "0.68rem",
    marginTop: theme.spacing(0.5),
  },
  ownerDirectChip: {
    backgroundColor: "rgba(245,158,11,0.15)",
    color: "#b45309",
  },
  ownerInheritedChip: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    color: theme.palette.text.secondary,
  },
  memberDirectChip: {
    backgroundColor: "rgba(59,130,246,0.12)",
    color: theme.palette.type === "dark" ? "#93c5fd" : "#1d4ed8",
  },
  memberInheritedChip: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(96,165,250,0.12)"
        : "rgba(96,165,250,0.18)",
    color: theme.palette.type === "dark" ? "#bfdbfe" : "#1e40af",
  },
  memberName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.25),
  },
  memberEmail: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    lineHeight: 1.3,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  legend: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

// ─── MemberTile ───────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<
  MemberCategory,
  { chipLabel: string; tooltip: string }
> = {
  "owner-direct": { chipLabel: "Owner", tooltip: "Direct owner" },
  "owner-inherited": {
    chipLabel: "Inherited owner",
    tooltip: "Inherited owner (from ancestor group)",
  },
  "member-direct": { chipLabel: "Member", tooltip: "Direct member" },
  "member-inherited": {
    chipLabel: "Inherited member",
    tooltip: "Inherited member (from descendant group)",
  },
};

const MemberTile: FC<{ entity: UserEntity; category: MemberCategory }> = ({
  entity,
  category,
}) => {
  const classes = useStyles();
  const displayName = entity.spec?.profile?.displayName ?? entity.metadata.name;
  const email = entity.spec?.profile?.email;
  const picture = entity.spec?.profile?.picture;
  const { chipLabel, tooltip } = CATEGORY_CONFIG[category];

  const badgeIcon = (() => {
    switch (category) {
      case "owner-direct":
        return (
          <StarIcon
            className={`${classes.badgeIcon} ${classes.ownerDirectIcon}`}
            fontSize="small"
          />
        );
      case "owner-inherited":
        return (
          <StarBorderIcon
            className={`${classes.badgeIcon} ${classes.ownerInheritedIcon}`}
            fontSize="small"
          />
        );
      case "member-direct":
        return (
          <PersonIcon
            className={`${classes.badgeIcon} ${classes.memberDirectIcon}`}
            fontSize="small"
          />
        );
      case "member-inherited":
        return (
          <GroupIcon
            className={`${classes.badgeIcon} ${classes.memberInheritedIcon}`}
            fontSize="small"
          />
        );
      default:
        return null;
    }
  })();

  const chipClass = (() => {
    switch (category) {
      case "owner-direct":
        return `${classes.chip} ${classes.ownerDirectChip}`;
      case "owner-inherited":
        return `${classes.chip} ${classes.ownerInheritedChip}`;
      case "member-direct":
        return `${classes.chip} ${classes.memberDirectChip}`;
      case "member-inherited":
        return `${classes.chip} ${classes.memberInheritedChip}`;
      default:
        return classes.chip;
    }
  })();

  return (
    <Card variant="outlined" className={classes.memberCard}>
      <CardActionArea
        component={EntityRefLink}
        entityRef={entity}
        style={{ padding: 0 }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          {/* Avatar + badge icon */}
          <div className={classes.avatarWrap}>
            <Avatar src={picture} alt={displayName} className={classes.avatar}>
              {!picture && displayName.slice(0, 2).toUpperCase()}
            </Avatar>
            {badgeIcon && (
              <Tooltip title={tooltip} placement="top">
                {badgeIcon}
              </Tooltip>
            )}
          </div>

          {/* Name */}
          <Typography className={classes.memberName} noWrap>
            {displayName}
          </Typography>

          {/* Email */}
          {email && (
            <Typography className={classes.memberEmail} noWrap>
              {email}
            </Typography>
          )}

          {/* Category chip */}
          <Chip label={chipLabel} size="small" className={chipClass} />
        </Box>
      </CardActionArea>
    </Card>
  );
};

// ─── GroupMembersCard ─────────────────────────────────────────────────────────

/**
 * Tarjeta unificada de miembros para la página de Overview de un grupo.
 *
 * Muestra todos los miembros en una cuadrícula de tiles e indica la categoría
 * de cada uno con un icono y un chip de color:
 *
 *   - **Owner directo** (★ ámbar + chip "Owner"):
 *     usuario asignado explícitamente como owner del grupo en Azure AD.
 *   - **Owner heredado** (☆ gris + chip "Inherited owner"):
 *     usuario que es owner de un grupo ancestro.
 *   - **Miembro directo** (👤 azul + chip "Member"):
 *     usuario que pertenece directamente a este grupo (sin ser owner).
 *   - **Miembro heredado** (👥 azul claro + chip "Inherited member"):
 *     usuario que pertenece a un grupo descendiente (bottom-up).
 *
 * Regla de prioridad: owner > member. Si un usuario es owner y a la vez
 * aparece como miembro heredado, se muestra como owner.
 *
 * Orden de visualización: owner-direct, owner-inherited, member-direct,
 * member-inherited; dentro de cada categoría, orden alfabético.
 *
 * Datos:
 *   - `hasMember` relations → lista efectiva de miembros
 *   - `graph.microsoft.com/owners-direct` → owners directos
 *   - `graph.microsoft.com/owners-inherited` → owners heredados
 *   - `graph.microsoft.com/members-inherited` → miembros heredados
 */
export const GroupMembersCard: FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const [search, setSearch] = useState("");

  const { entities: rawEntities, loading } = useRelatedEntities(entity, {
    type: RELATION_HAS_MEMBER,
    kind: "User",
  });

  const ownersDirect = parseRefSet(
    entity.metadata.annotations?.[ANNOTATION_OWNERS_DIRECT],
  );
  const ownersInherited = parseRefSet(
    entity.metadata.annotations?.[ANNOTATION_OWNERS_INHERITED],
  );
  const membersInherited = parseRefSet(
    entity.metadata.annotations?.[ANNOTATION_MEMBERS_INHERITED],
  );

  const members = (rawEntities ?? []) as UserEntity[];
  const filtered = members.filter((m) => matchesSearch(m, search));

  // Orden: owner-direct (0), owner-inherited (1), member-direct (2), member-inherited (3)
  const categoryRank: Record<MemberCategory, number> = {
    "owner-direct": 0,
    "owner-inherited": 1,
    "member-direct": 2,
    "member-inherited": 3,
  };

  const sorted = [...filtered].sort((a, b) => {
    const ca = getCategory(a, ownersDirect, ownersInherited, membersInherited);
    const cb = getCategory(b, ownersDirect, ownersInherited, membersInherited);
    if (categoryRank[ca] !== categoryRank[cb]) {
      return categoryRank[ca] - categoryRank[cb];
    }
    const na = a.spec?.profile?.displayName ?? a.metadata.name;
    const nb = b.spec?.profile?.displayName ?? b.metadata.name;
    return na.localeCompare(nb);
  });

  // ¿Hay alguna categoría especial activa para mostrar la leyenda?
  const hasOwners = ownersDirect.size > 0 || ownersInherited.size > 0;
  const hasInheritedMembers = membersInherited.size > 0;
  const showLegend = hasOwners || hasInheritedMembers;

  return (
    <Box>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">
          Members ({filtered.length} of {members.length})
        </Typography>
      </div>

      {/* Leyenda — solo cuando hay categorías especiales */}
      {showLegend && (
        <div className={classes.legend}>
          {hasOwners && (
            <>
              <span className={classes.legendItem}>
                <StarIcon style={{ fontSize: 14, color: "#f59e0b" }} />
                Direct owner
              </span>
              <span className={classes.legendItem}>
                <StarBorderIcon style={{ fontSize: 14 }} />
                Inherited owner
              </span>
            </>
          )}
          <span className={classes.legendItem}>
            <PersonIcon style={{ fontSize: 14, color: "#3b82f6" }} />
            Direct member
          </span>
          {hasInheritedMembers && (
            <span className={classes.legendItem}>
              <GroupIcon style={{ fontSize: 14, color: "#60a5fa" }} />
              Inherited member
            </span>
          )}
        </div>
      )}

      {/* Búsqueda */}
      <TextField
        className={classes.search}
        variant="outlined"
        size="small"
        fullWidth
        placeholder="Search members..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Contenido */}
      {loading && <Progress />}
      {!loading && sorted.length === 0 && (
        <Typography className={classes.emptyState}>
          {search ? "No members match your search." : "No members."}
        </Typography>
      )}
      {!loading && sorted.length > 0 && (
        <Grid container spacing={2}>
          {sorted.map((member) => (
            <Grid item key={stringifyEntityRef(member)} xs={6} sm={4} md={3}>
              <MemberTile
                entity={member}
                category={getCategory(
                  member,
                  ownersDirect,
                  ownersInherited,
                  membersInherited,
                )}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
